import React, { useState, useContext, useEffect } from 'react';
import { firstBy } from 'thenby';
import usePrevious from '../Hooks/usePrevious';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import AppContext from '../Hooks/AppContext';
import { useParams } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * Static core components
 */
import Highlighted from './Core/Highlighted';
import Navigation from './Core/Navigation';

/**
 * Dynamic dialog components
 */
import Entities from '../Views/Entities';
import Printer from './Printer';

const defaultFilter = {
  idBranch: '0',
  idPrinterType: '0',
  text: '',
};
const defaultDeps = {
  branches: [],
  printerDrivers: [],
  printerTypes: [],
};
const defaultSelection = {
  entity: null,
  op: null,
  entities: [],
  anchorEl: null,
};

const Printers = props => {
  /**
   * Constants
   */

  /**
   * States
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);
  const [data, setData] = useState(null);
  const [deps, setDeps] = useState(defaultDeps);
  const [defs, setDefs] = useState(null);
  const [selection, setSelection] = useState(defaultSelection);

  /**
   * Hooks
   */
  const ac = useContext(AppContext);
  const params = useParams();
  const prev = usePrevious({params});

  /**
   * Effects
   */
  useEffect(() => {
    if (JSON.stringify(params) === JSON.stringify(prev?.params)) return;

    ac.ajax('getPrinters')
      .then(response => {
        if (response.status.ok) {
          setFilter(getDefaultFilter());
          setData(response.result.data.printers);
          setDeps(response.result.dependencies);
          setDefs(response.result.defaultValues);

          setIsLoaded(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /**
   * Getters
   */
  const getDefaultFilter = () => ({
    ...defaultFilter,
    idBranch: params.idBranch ? params.idBranch : defaultFilter.idBranch,
  });
  const getBranches = () => deps.branches.sort(firstBy('branch'));
  const getPrinterTypes = () => deps.printerTypes.sort(firstBy('printerType'));

  /**
   * Handlers
   */
  const handleFilterChange = () => evt => {
    setFilter({
      ...filter,
      [evt.target.name]: evt.target.value,
    });
  };
  const handleFilterTextReset = () => () => {
    setFilter({
      ...filter,
      text: defaultFilter.text,
    });
  };
  const handleFilterReset = () => () => {
    setFilter(getDefaultFilter());
  };
  const handleSelectionChange = (value = null, showMenu = null) => (evt = null) => {
    if (evt !== null && 'stopPropagation' in evt) evt.stopPropagation();

    setSelection(prevState => ({
      ...prevState,
      ...value,
      anchorEl: showMenu === true ? evt.currentTarget : showMenu === false ? null : selection.anchorEl,
    }));
  };
  const handleReload = (response, newSelection = null) => {
    setData(response.result.data.printers);
    setDeps(response.result.dependencies);
    setDefs(response.result.defaultValues);

    handleSelectionChange(
      newSelection
        ? newSelection
        : response.result.lastId
          ? {
              ...defaultSelection,
              entity: response.result.data.printers.find(item => item.id === response.result.lastId),
              entities: [response.result.lastId],
            }
          : selection
    )();
  };

  /**
   * Subcomponents render
   */
  const renderFilter = () => (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={4}>
        <TextField select
          disabled={ac.isLoading}
          label='Punct de lucru'
          placeholder='Punct de lucru'
          name='idBranch'
          value={filter.idBranch}
          onChange={handleFilterChange()}
        >
          <MenuItem value={defaultFilter.idBranch}><Typography variant='body1' fontStyle='italic' fontWeight='bold'>Toate</Typography></MenuItem>
          {getBranches().map(item => (
            <MenuItem key={item.id} value={item.id}><Typography variant='body1'>{item.branch}</Typography></MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField select
          disabled={ac.isLoading}
          label='Tip imprimanta'
          placeholder='Tip imprimanta'
          name='idPrinterType'
          value={filter.idPrinterType}
          onChange={handleFilterChange()}
        >
          <MenuItem value={defaultFilter.idPrinterType}><Typography variant='body1' fontStyle='italic' fontWeight='bold'>Toate</Typography></MenuItem>
          {getPrinterTypes().map(item => (
            <MenuItem key={item.id} value={item.id}><Typography variant='body1'>{item.printerType}</Typography></MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <TextField
          InputProps={{
            endAdornment: filter.text !== defaultFilter.text
              ? <InputAdornment position='end'>
                  <IconButton edge='end' size='small' color='error'
                    onClick={handleFilterTextReset()}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              : null,
          }}
          disabled={ac.isLoading || data?.length === 0}
          label='Filtrare rapida'
          placeholder='Filtrare rapida'
          name='text'
          value={filter.text}
          onChange={handleFilterChange()}
        />

        <Tooltip title='Reseteaza filtrele la valoarile initiale'>
          <div>
            <IconButton color='error'
              disabled={JSON.stringify(filter) === JSON.stringify(getDefaultFilter())}
              onClick={handleFilterReset()}
            >
              <FilterAltOffIcon />
            </IconButton>
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  );

  /**
   * Renderer
   */
  if (!isLoaded) {
    return null;
  }

  return (
    <Container maxWidth='md' fixed>
      <Navigation
        paths={[
          {text: 'Acasa'                     , path: '/'                   , icon: 'Home'      },
          {text: 'Administrare'              , path: '/Admin'              , icon: 'Settings'  },
          {text: 'Beneficiari'               , path: '/Admin/Beneficiaries', icon: 'Store'     },
          {text: 'Puncte de lucru (optional)', path: -1                    , icon: 'Storefront'},
          {text: 'Imprimante'                , path: null                  , icon: 'Print'     },
        ]}
      />

      {renderFilter()}

      <Entities defaultFilter={defaultFilter} filter={filter} data={data} deps={deps} selection={selection}
        sortBy={['branch', 'printerType', 'printer']}
        groupBy={filter.idBranch === defaultFilter.idBranch ? 'branch' : 'printerType'}
        renderEntity={{
          avatar: entity => <Avatar alt={entity.printerType}>{entity.printerType[0]}</Avatar>,
          icon: null,
          textPrimary: entity => (
            <Highlighted highlight={filter.text} variant='body2' color={entity.isEnabled ? 'textPrimary' : 'rgba(0, 0, 0, 0.26)'} sx={{flexGrow: 1, fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{entity.printer}</Highlighted>
          ),
          textSecondary: [
            entity => <Highlighted highlight={filter.text}>{entity.printerType} {entity.manufacturer} {entity.model}</Highlighted>,
            entity => <Highlighted highlight={filter.text}>SN: {entity.serialNumber}, IP: {entity.ip}</Highlighted>,
            entity => <Highlighted highlight={filter.text}>Driver: {entity.printerDriver}</Highlighted>,
          ],
          textIcons: [
            entity => ({text: 'Aceasta imprimanta este activa'   , icon: 'CheckCircle', color: 'primary', when: entity.isEnabled }),
            entity => ({text: 'Aceasta imprimanta nu este activa', icon: 'Cancel'     , color: 'error'  , when: !entity.isEnabled}),
          ],
        }}
        contextualMenu={[
          entity => ({text: 'Modifica'    , icon: null, action: 'editEntity'    , when: true             }),
          entity => ({text: 'NIY - Dezactiveaza', icon: null, action: 'disablePrinter', when: entity.isEnabled }),
          entity => ({text: 'NIY - Activeaza'   , icon: null, action: 'enablePrinter' , when: !entity.isEnabled}),
        ]}
        newEntity={{
          op: 'editEntity',
          entity: {
            ...defs.printer,
            idBranch: filter.idBranch === defaultFilter.idBranch ? defs.printer.idBranch : filter.idBranch,
            printer: filter.text === defaultFilter.text ? defs.printer.text : filter.text,
            idPrinterType: filter.idPrinterType === defaultFilter.idPrinterType ? defs.printer.idPrinterType : filter.idPrinterType,
          },
        }}
        onChangeSelection={setSelection}
      />

      {/**
       * Add/edit entity
       */}
      {Boolean(selection.entity) && selection.op === 'editEntity' &&
        <Printer
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }
    </Container>
  );
};

export default Printers;